// EntraConsentBanner.jsx - tells an administrator when the Microsoft Entra
// app registration is short of a permission Govern360 asks for, before they
// go looking for the capability it is missing.
//
// Sibling of ConsentBanner.jsx, which does the same for Power Platform.
//
// WHY THIS ONE HAS TWO STEPS
// The Power Platform banner reauthorizes in one click because its authorize
// URL carries the scopes inline. Entra cannot do that: /adminconsent grants
// only what is already DECLARED on the app registration. Offering one click
// here would show an admin a consent screen listing the permissions they
// already have - they would approve it, nothing would change, and everyone
// would believe it was done. So: declare, then consent.
//
// States what is missing in terms of what they can do. Scope names appear
// only inside the script, where they are the thing being typed.
//
// Renders nothing unless a permission is genuinely absent. Blocks nothing.
//
// G360_K463 - after "2. Grant consent" Microsoft lands on its own blank
// page (entra.microsoft.com/TokenAuthorize, the reply address the connect
// script registers). The banner re-checks when the person comes back to
// this tab, and on "Check again". The status function reads what Entra
// has granted, so the check can clear.
//
// G360_K786 - K463 told the reader that blank page "IS the success". That
// was wrong, and it made this banner look permanently broken.
//
// Microsoft shows the SAME blank page whether or not anything changed.
// /adminconsent grants only what the app registration already declares,
// so clicking step 2 before step 1 approves a set containing nothing new:
// blank page, no change, banner still up. Someone following the old copy
// read the blank page as success, came back to an unchanged banner, and
// concluded the product was broken - reasonably.
//
// So: the blank page is now described as what it is (Microsoft finishing,
// carrying no information), the permission state is the only thing
// treated as a signal, and this polls instead of waiting to be visited -
// because per k249 the person who grants is frequently not the person
// sitting at this console, and a focus-based re-check can never fire for
// them.

import { useEffect, useState } from 'react';
import { ShieldAlert, ExternalLink, Copy, Check, RefreshCw, CheckCircle2 } from 'lucide-react';
import { supabase } from '@govern360/shared';
import { connectionWarning } from '../lib/connectionWarning.js';

/* G360_K786 - GRAPH_APP_ID is gone. setupScript used to hardcode Graph's
   app id and resolve every permission against it, which is exactly why a
   non-Graph permission could not be declared. Each group now resolves its
   own service principal by identifier URI. */

/* G360_K786 - the page Microsoft should come back to instead of its own
   blank one. Per environment, because a redirect_uri has to match a
   registered reply address EXACTLY, and dev and prod are different hosts. */
export function consentCallbackUrl() {
  try {
    return window.location.origin + '/.netlify/functions/ms-consent-callback';
  } catch (e) { return ''; }
}

function setupScript(state) {
  const cb = consentCallbackUrl();
  /* G360_K786 - resolve each permission against the API it actually lives
     on. This used to resolve everything against the Graph service
     principal and throw if a name was not found there - so the moment kit
     785 added Machine.Read.All (WindowsDefenderATP), step 1 could not
     succeed at all, and step 2 had nothing to grant. Falls back to the
     flat list for an older status function that does not send groups. */
  const groups = Array.isArray(state.missing_by_resource) && state.missing_by_resource.length
    ? state.missing_by_resource
    : [{ resource: 'https://graph.microsoft.com', scopes: state.missing_scopes || [] }];

  const lines = [
    "Connect-MgGraph -Scopes 'Application.ReadWrite.All','Directory.ReadWrite.All'",
    '',
    "$clientId = '" + (state.client_id || '') + "'",
    "$app = Get-MgApplication -Filter \"appId eq '$clientId'\"",
    'if (-not $app) { throw "App registration $clientId not found in this tenant - stopping." }',
    '$rra = @($app.RequiredResourceAccess)',
    ''
  ];

  groups.forEach(function (g, n) {
    const wanted = (g.scopes || []).map(function (s) { return "'" + s + "'"; }).join(',');
    lines.push(
      '# ' + (g.scopes || []).join(', ') + '  on  ' + g.resource,
      '$uri' + n + ' = \'' + g.resource + '\'',
      '$want' + n + ' = @(' + wanted + ')',
      // Resolve the API's service principal by its identifier URI rather
      // than a hardcoded app id, so this works for Graph, Defender and
      // anything added later without a GUID table.
      '$sp' + n + ' = Get-MgServicePrincipal -Filter "servicePrincipalNames/any(n:n eq \'$uri' + n + '\')"',
      'if (-not $sp' + n + ') { throw "Could not find the API $uri' + n + ' in this tenant - stopping." }',
      '$roles' + n + ' = $sp' + n + '.AppRoles | Where-Object { $want' + n + ' -contains $_.Value }',
      'if ($roles' + n + '.Count -ne $want' + n + '.Count) { throw "Could not resolve every permission on $uri' + n + ' - stopping." }',
      '$entry' + n + ' = $rra | Where-Object { $_.ResourceAppId -eq $sp' + n + '.AppId }',
      'if (-not $entry' + n + ') {',
      '  $entry' + n + ' = @{ ResourceAppId = $sp' + n + '.AppId; ResourceAccess = @() }',
      '  $rra = $rra + $entry' + n,
      '}',
      'foreach ($r in $roles' + n + ') {',
      '  if ($entry' + n + '.ResourceAccess.Id -notcontains $r.Id) {',
      "    $entry" + n + ".ResourceAccess += @{ Id = $r.Id; Type = 'Role' }",
      '  }',
      '}',
      ''
    );
  });

  return lines.concat([
    'Update-MgApplication -ApplicationId $app.Id -RequiredResourceAccess $rra',
    '',
    '# G360_K786 - also register the page Microsoft should return to, so the',
    '# approval ends on a Govern360 confirmation instead of a blank Microsoft',
    '# page. Additive: existing reply addresses are kept.',
    '#',
    '# $app.Web can be absent entirely - a real app registration here was',
    '# observed with NO reply addresses at all. @($app.Web.RedirectUris) on',
    '# that app yields @($null) and would register a null URI, so Web and',
    '# RedirectUris are both tested before use.',
    "$cb   = '" + cb + "'",
    '$uris = @()',
    'if ($app.Web -and $app.Web.RedirectUris) { $uris = @($app.Web.RedirectUris) }',
    'if ($uris -notcontains $cb) {',
    '  $uris = $uris + $cb',
    '  Update-MgApplication -ApplicationId $app.Id -Web @{ RedirectUris = $uris }',
    "  Write-Host ('Reply address registered: ' + $cb)",
    '}',
    '',
    "Write-Host 'Declared. Now click Grant consent in Govern360.'"
  ]).join('\n');
}

export default function EntraConsentBanner() {
  const [state, setState] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedAdmin, setCopiedAdmin] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // G360_K463 - 'waiting' once Grant consent was clicked; 'checking' while
  // a re-check runs; 'still' when a re-check found it not granted yet;
  // 'done' when it has landed.
  const [phase, setPhase] = useState(null);

  async function check(isRecheck) {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) return null;
      const r = await fetch('/.netlify/functions/entra-consent-status', {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!r.ok) { if (!isRecheck) setLoadError(true); return null; }
      return await r.json();
    } catch (e) { if (!isRecheck) setLoadError(true); return null; }
  }

  useEffect(() => {
    let live = true;
    (async () => {
      const j = await check(false);
      /* g360-k347 - render for a credential failure as well as for stale
         scopes. Previously only the scope case reached setState, so the
         failure that stops every collector showed nothing at all. */
      if (live && j && (j.credential_failed || (j.connected && !j.current))) setState(j);
    })();
    return () => { live = false; };
  }, []);

  async function recheck() {
    setPhase('checking');
    const j = await check(true);
    if (!j) { setPhase('still'); return; }
    if (j.credential_failed) { setState(j); setPhase(null); return; }
    if (j.connected && !j.current) { setState(j); setPhase('still'); return; }
    setPhase('done');
  }

  // Coming back from the Microsoft tab is the moment to look again.
  useEffect(() => {
    if (phase !== 'waiting' && phase !== 'still') return undefined;
    function onBack() { if (document.visibilityState === 'visible') recheck(); }
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    return () => {
      document.removeEventListener('visibilitychange', onBack);
      window.removeEventListener('focus', onBack);
    };
  }, [phase]);

  /* G360_K786 - and poll, because coming back is not guaranteed. The
     admin may be a different person entirely, or may grant it on their
     own machine; no focus event ever reaches this tab. Every 5s for about
     two minutes, then stop - "Check again" is still there, and any reload
     re-checks on mount. Calls check() rather than recheck() so it does
     not churn the phase the effect depends on. */
  useEffect(() => {
    if (phase !== 'waiting' && phase !== 'still') return undefined;
    let live = true;
    let tries = 0;
    const id = setInterval(async () => {
      if (!live) return;
      tries += 1;
      if (tries > 24) { clearInterval(id); return; }
      const j = await check(true);
      if (!live || !j) return;
      if (j.credential_failed) { setState(j); setPhase(null); clearInterval(id); return; }
      if (j.connected && j.current) { setPhase('done'); clearInterval(id); }
    }, 5000);
    return () => { live = false; clearInterval(id); };
  }, [phase]);

  if (phase === 'done') {
    return (
      <div className="banner" style={{ alignItems: 'flex-start', marginBottom: 14, background: 'var(--g-success-bg)', color: 'var(--g-success)' }}>
        <CheckCircle2 size={16} />
        <span style={{ flex: 1 }}>
          <strong>Permission granted.</strong>{' '}
          Microsoft now confirms it for this app. Govern360 picks it up on the next collection run.
        </span>
        <button className="btn outline" style={{ height: 30, padding: '0 12px', fontSize: 12, whiteSpace: 'nowrap' }}
          onClick={() => { setPhase(null); setState(null); }}>
          Dismiss
        </button>
      </div>
    );
  }

  /* g360-k347 - the credential case returns FIRST and returns ONLY this.
     A consent script below would send somebody to grant permissions to an
     app that cannot authenticate, which is work that cannot help. That is
     what happened: three rounds of running a consent script for a stored
     secret that was never being updated. */
  if (state && state.credential_failed) {
    return (
      <div className="banner warn" style={{ alignItems: "flex-start", marginBottom: 14 }}>
        <ShieldAlert size={16} />
        <span style={{ flex: 1 }}>
          <strong>{connectionWarning(state.last_error)}</strong>
          <div style={{ fontSize: 12, marginTop: 5, lineHeight: 1.6 }}>
            This is a credential problem, not a permissions one &mdash; granting consent will not
            change it. Purview, Intune, identity and Copilot activity all use this one connection.
          </div>
          <ol style={{ fontSize: 12, margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.65 }}>
            <li>In the Azure portal open <strong>App registrations</strong> and search for
              {" "}<code style={{ fontSize: 11 }}>{state.client_id}</code></li>
            <li><strong>Certificates &amp; secrets</strong> &rarr; <strong>New client secret</strong> &rarr; 24 months &rarr; Add</li>
            <li>Copy the <strong>Value</strong> column, not Secret ID. It is shown once and masked afterwards.</li>
            <li>Open <strong>Connections</strong>, click <strong>Reconnect</strong> on the Microsoft card and paste it.</li>
          </ol>
        </span>
      </div>
    );
  }

  if (!state && loadError) {
    return (
      <div className="banner warn" style={{ alignItems: "flex-start", marginBottom: 14 }}>
        <ShieldAlert size={16} />
        <span style={{ flex: 1 }}>
          <strong>Permission state could not be read.</strong>{" "}
          <span style={{ color: "var(--g-text-2)" }}>
            This is not a statement that your permissions are in order - the check itself
            did not complete. Reload the page, and tell your Govern360 contact if it persists.
          </span>
        </span>
      </div>
    );
  }
  if (!state) return null;

  // g360-k249: the person reading this console is frequently not the person
  // who can act on it. Copying the script to THEIR clipboard is a dead end.
  // This copies a block they can paste to whoever holds the role.
  async function copyForAdmin() {
    const parts = [
      "Govern360 needs one read-only Microsoft Graph permission declared on our Entra app registration.",
      "",
      "Step 1 - run this in PowerShell, signed in as a Global Administrator or Application Administrator:",
      "",
      setupScript(state),
      "",
      "Step 2 - open this link and approve:",
      state.consent_url || "(ask your Govern360 contact for the consent link)",
      "Microsoft ends on a blank page either way - it does not tell you whether anything changed. Close it.",
      "Step 1 MUST be done first: approving cannot grant a permission the app registration does not declare.",
      "",
      "Nothing currently working is affected. This only adds what is listed above, and it is read-only."
    ];
    try {
      await navigator.clipboard.writeText(parts.join("\n"));
      setCopiedAdmin(true);
      setTimeout(function () { setCopiedAdmin(false); }, 2000);
    } catch (e) {
      setCopiedAdmin(false);
    }
  }

  async function copyScript() {
    try {
      await navigator.clipboard.writeText(setupScript(state));
      setCopied(true);
      setTimeout(function () { setCopied(false); }, 2000);
    } catch (e) {
      setCopied(false);
    }
  }

  /* G360_K786 - add redirect_uri ONLY when this exact address is already a
     registered reply address on the app. A redirect_uri Entra does not
     recognise fails the whole request with AADSTS50011, so an unregistered
     guess would break consent rather than improve it. Step 1 registers it;
     until then this is byte-for-byte the old URL. */
  const callback = consentCallbackUrl();
  const landsOnUs = !!callback && (state.redirect_uris || []).indexOf(callback) !== -1;
  const consentHref = landsOnUs
    ? state.consent_url + '&redirect_uri=' + encodeURIComponent(callback)
    : state.consent_url;

  return (
    <div className="banner warn" style={{ alignItems: 'flex-start', marginBottom: 14 }}>
      <ShieldAlert size={16} />
      <span style={{ flex: 1 }}>
        <strong>This needs your Entra administrator.</strong>{" "}
        A Global Administrator or Application Administrator has to do both steps below.{" "}
        Granting it lets Govern360 {state.missing.join(', and ')}.
        <span style={{ color: 'var(--g-text-2)' }}>
          {/* G360_K189 quantifier derived from the count, not hardcoded. The
              sentence read "both permissions" while kit 188 had reduced the
              flaggable set to one. */}
          {' '}Nothing you have today stops working - this only adds what is listed,
          and {state.missing.length === 1 ? 'the permission is' : 'all ' + state.missing.length + ' permissions are'} read-only. Two steps, because Entra grants only
          what the app registration already declares: run the script to declare it,
          then approve.
        </span>
        {phase === 'waiting' && (
          <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.6 }}>
            {landsOnUs ? (
              <>Microsoft will bring you back to a Govern360 page when you approve. This banner is
              also checking every few seconds and clears itself once the permission lands.</>
            ) : (
              <>Microsoft ends on a <strong>blank page</strong> either way &mdash; it does not tell
              you whether anything changed. Close that tab; this banner is checking every few
              seconds and will say so itself when the permission lands. If nothing changes within a
              minute or two, <strong>step 1 has not been run</strong> &mdash; approving cannot grant
              a permission the app registration does not declare, and step 1 is also what replaces
              that blank page with a Govern360 one.</>
            )}
          </div>
        )}
        {phase === 'checking' && (
          <div style={{ fontSize: 12, marginTop: 6 }}>Checking with Microsoft&hellip;</div>
        )}
        {phase === 'still' && (
          <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.6 }}>
            Microsoft does not show the permission as granted yet. It can take a few minutes after
            approval to appear &mdash; check again shortly. If it never appears, run step 1 first, then approve again.
          </div>
        )}
      </span>
      <button className="btn outline" style={{ height: 30, padding: '0 12px', fontSize: 12, whiteSpace: 'nowrap', marginRight: 6 }}
        onClick={copyScript}>
        {copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copied' : '1. Copy script'}
      </button>
      <button className="btn outline" style={{ height: 30, padding: "0 12px", fontSize: 12, whiteSpace: "nowrap", marginRight: 6 }}
        onClick={copyForAdmin}>
        {copiedAdmin ? <Check size={14} /> : <Copy size={14} />}{copiedAdmin ? "Copied" : "Copy for your admin"}
      </button>
      {state.consent_url && (
        <a className="btn primary" style={{ height: 30, padding: '0 12px', fontSize: 12, whiteSpace: 'nowrap' }}
          href={consentHref} target="_blank" rel="noreferrer"
          onClick={() => setPhase('waiting')}>
          <ExternalLink size={14} />2. Grant consent (after step 1)
        </a>
      )}
      {(phase === 'waiting' || phase === 'still' || phase === 'checking') && (
        <button className="btn outline" style={{ height: 30, padding: '0 12px', fontSize: 12, whiteSpace: 'nowrap', marginLeft: 6 }}
          onClick={recheck} disabled={phase === 'checking'}>
          <RefreshCw size={14} />Check again
        </button>
      )}
    </div>
  );
}
